import type { ChapterSummary, JobSummary } from "@contract";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { StateChip } from "../../../components/control/StateChip";
import { Card } from "../../../components/data/Card";
import { type Column, DataTable } from "../../../components/data/DataTable";
import { ProgressBar } from "../../../components/feedback/ProgressBar";
import { Duration } from "../../../components/format/Duration";
import { Money } from "../../../components/format/Money";
import { Num } from "../../../components/format/Num";
import { useBookContext } from "../bookContext";
import {
  chapterAction,
  chapterActionPath,
  chapterProgress,
  chapterReviewPath,
  jobsByChapter,
} from "../chapterAction";

/**
 * Fixed 40 px for the number, then weighted fractions with a floor under each:
 * the German words ("wird vertont", "ausgeschlossen", "Fehler ansehen") are
 * about a third longer than the English ones, and a bare fraction hands the
 * state column 83 px at 1024 px, which clips "ausgeschlossen". The floors add
 * up to MIN_TABLE_WIDTH, below which the card scrolls sideways rather than
 * truncating cells that have no tooltip. The action column is fixed because
 * its widest label is a button, not text that may wrap.
 */
export const CHAPTER_TEMPLATE =
  "40px minmax(160px, 1.6fr) minmax(120px, 1.2fr) minmax(80px, 0.8fr) minmax(72px, 0.7fr) minmax(96px, 1fr) 132px";
/** The sum of the template's floors plus the gaps between them; the table scrolls below it. */
export const MIN_TABLE_WIDTH = "min-w-[760px]";

/** A Chapter the worker gave up on is tinted; nothing else is. */
const rowTone = (chapter: ChapterSummary) =>
  chapter.state === "failed" ? ("danger" as const) : ("none" as const);

/**
 * Chapter Audio: its length, and a warn word when an edit made it stale. The
 * two stack rather than sit side by side, because the cell truncates and
 * "Audio veraltet" next to "21:48" does not fit the column in either language.
 */
function AudioCell({ chapter }: { chapter: ChapterSummary }) {
  const { t } = useTranslation("overview");
  if (!chapter.audio) return <span className="text-ink-3">{t("chapters.none")}</span>;
  return (
    <span className="flex flex-col items-end">
      <Duration ms={chapter.audio.durationMs} />
      {chapter.audio.stale ? (
        <span className="text-label text-warn">{t("chapters.staleAudio")}</span>
      ) : null}
    </span>
  );
}

/** The Chapter's title, linking to its Review screen from the first Narration Script on. */
function TitleCell({ bookId, chapter }: { bookId: string; chapter: ChapterSummary }) {
  const { t } = useTranslation("overview");
  const path = chapterReviewPath(bookId, chapter);
  const calibration = chapter.isCalibration ? (
    <span className="shrink-0 font-mono text-label text-accent">{t("chapters.calibration")}</span>
  ) : null;
  return (
    <span className="flex min-w-0 items-baseline gap-2">
      {path ? (
        <Link to={path} className="truncate text-ink hover:text-accent hover:underline">
          {chapter.title}
        </Link>
      ) : (
        <span className="truncate">{chapter.title}</span>
      )}
      {calibration}
    </span>
  );
}

/**
 * The one action the row offers, always a link: a parked Chapter points at the
 * failures card, which carries the error, the attempt count, and the single
 * retry button for that job.
 */
function ActionCell({ bookId, chapter }: { bookId: string; chapter: ChapterSummary }) {
  const { t } = useTranslation("overview");
  const action = chapterAction(chapter);
  const path = action ? chapterActionPath(bookId, chapter) : null;
  if (!action || !path) return null;
  if (action === "listen") {
    // The route answers 302 to a presigned URL; a new tab keeps the Book page
    // and its poll alive while the browser starts the download.
    return (
      <a href={path} target="_blank" rel="noreferrer" className="text-accent hover:underline">
        {t("chapterAction.listen")}
      </a>
    );
  }
  if (action === "failure") {
    return (
      <a href={path} className="text-danger hover:underline">
        {t("chapterAction.failure")}
      </a>
    );
  }
  return (
    <Link to={path} className="text-accent hover:underline">
      {action === "review" ? t("chapterAction.review") : t("chapterAction.watch")}
    </Link>
  );
}

/** Every Chapter of the Book with its state, size, cost, and audio: the table DESIGN keeps referring to. */
export function ChaptersCard() {
  const { t } = useTranslation("overview");
  const { book } = useBookContext();
  // One pass over the jobs for the whole table: a find() per row turns a Book
  // with many Chapters and many running jobs into a quadratic scan every poll.
  const jobs: ReadonlyMap<string, JobSummary> = jobsByChapter(book.activeJobs);

  const columns: ReadonlyArray<Column<ChapterSummary>> = [
    {
      id: "number",
      header: t("chapters.colNumber"),
      className: "font-mono text-data text-ink-2",
      cell: (chapter) => chapter.number ?? "",
    },
    {
      id: "title",
      header: t("chapters.colTitle"),
      cell: (chapter) => <TitleCell bookId={book.id} chapter={chapter} />,
    },
    {
      id: "state",
      header: t("chapters.colState"),
      cell: (chapter) => {
        const job = jobs.get(chapter.id);
        const fraction = chapterProgress(chapter, job);
        // A Chapter waiting for its turn says so: "in Arbeit" over many
        // minutes without a word about the queue reads as nothing happening.
        const queued =
          chapter.state === "refining" && job !== undefined && job.status === "queued"
            ? t("chapters.queued")
            : null;
        const chunks =
          job?.status === "running" && job.progress && job.progress.total > 1
            ? t("chapters.progress", { done: job.progress.done, total: job.progress.total })
            : null;
        return (
          <span className="flex min-w-0 flex-col gap-1">
            <StateChip kind="chapter" state={chapter.state} />
            {chapter.approvalOrigin ? (
              <span className="text-label text-ink-2">
                {chapter.approvalOrigin === "automatic"
                  ? t("chapters.automatic")
                  : t("chapters.manual")}
              </span>
            ) : null}
            {queued ? <span className="text-label text-ink-3">{queued}</span> : null}
            {fraction === null ? null : <ProgressBar value={fraction} size="sm" />}
            {chunks ? <span className="font-mono text-label text-ink-3">{chunks}</span> : null}
          </span>
        );
      },
    },
    {
      id: "characters",
      header: t("chapters.colCharacters"),
      align: "right",
      className: "font-mono text-data text-ink-2",
      cell: (chapter) =>
        chapter.scriptCharacters === null ? (
          t("chapters.none")
        ) : (
          <Num value={chapter.scriptCharacters} />
        ),
    },
    {
      id: "estimate",
      header: t("chapters.colEstimate"),
      align: "right",
      className: "font-mono text-data text-ink-2",
      cell: (chapter) =>
        chapter.estimate === null ? (
          t("chapters.none")
        ) : (
          <Money cents={chapter.estimate.cents} approx />
        ),
    },
    {
      id: "audio",
      header: t("chapters.colAudio"),
      align: "right",
      className: "font-mono text-data text-ink-2",
      cell: (chapter) => <AudioCell chapter={chapter} />,
    },
    {
      id: "action",
      header: "",
      align: "right",
      cell: (chapter) => <ActionCell bookId={book.id} chapter={chapter} />,
    },
  ];

  return (
    <Card label={t("chapters.label")} padding="none">
      <div className="overflow-x-auto">
        <DataTable
          className={MIN_TABLE_WIDTH}
          columns={columns}
          rows={book.chapters}
          rowKey={(chapter) => chapter.id}
          template={CHAPTER_TEMPLATE}
          empty={t("chapters.empty")}
          rowTone={rowTone}
        />
      </div>
      <div className="px-6 py-3 font-mono text-label text-ink-2">
        {t("chapters.totals", { included: book.counts.included, excluded: book.counts.excluded })}
      </div>
    </Card>
  );
}
